import { useEffect, useRef, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

export interface NotificationRow {
  id: string;
  user_id: string | null;
  audience: "user" | "admin";
  type: string;
  title: string;
  body: string | null;
  link: string | null;
  metadata: Record<string, unknown>;
  read_at: string | null;
  created_at: string;
}

import { useQuery, useQueryClient } from "@tanstack/react-query";

export function useNotifications() {
  const { user, isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const seenIdsRef = useRef<Set<string>>(new Set());
  const reconnectAttemptsRef = useRef(0);
  const wasDisconnectedRef = useRef(false);

  const {
    data: items = [],
    isLoading: loading,
    error,
    refetch: refresh,
  } = useQuery({
    queryKey: ["notifications", user?.id, isAdmin],
    queryFn: async () => {
      if (!user) return [];

      let query = supabase
        .from("notifications")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(100);

      // Filter based on audience and ownership
      if (isAdmin) {
        query = query.or(
          `user_id.eq.${user.id},audience.eq.admin,and(user_id.is.null,audience.eq.user)`,
        );
      } else {
        query = query.or(
          `user_id.eq.${user.id},and(user_id.is.null,audience.eq.user)`,
        );
      }

      const { data, error: dbError } = await query;
      if (dbError) throw dbError;
      const rows = (data || []) as NotificationRow[];
      rows.forEach((r) => seenIdsRef.current.add(r.id));
      return rows;
    },
    enabled: !!user,
  });

  useEffect(() => {
    if (!user) return;

    let channel: ReturnType<typeof supabase.channel> | null = null;
    let reconnectTimer: number | null = null;
    let cancelled = false;

    const handleInsert = (payload: { new: NotificationRow }) => {
      const row = payload.new;
      if (!row || seenIdsRef.current.has(row.id)) return;
      seenIdsRef.current.add(row.id);

      // Drop admin-only notifications for non-admin users before touching
      // the cache — prevents leaking admin notification content into the
      // non-admin user's notification list.
      if (row.audience === "admin" && !isAdmin) return;

      // Update local cache immediately for a snappy UI
      queryClient.setQueryData(
        ["notifications", user.id, isAdmin],
        (prev: NotificationRow[] = []) => [row, ...prev],
      );

      toast.info(row.title, { description: row.body || undefined });
    };

    const handleUpdate = (payload: { new: NotificationRow }) => {
      const row = payload.new;
      if (!row) return;
      queryClient.setQueryData(
        ["notifications", user.id, isAdmin],
        (prev: NotificationRow[] = []) =>
          prev.map((n) => (n.id === row.id ? row : n)),
      );
    };

    const connect = () => {
      if (cancelled) return;

      // Supabase Realtime postgres_changes payloads use a generic Record type.
      // We cast once at the boundary rather than using `as never` on every subscription.
      type RealtimePayload = { new: Record<string, unknown> };
      const onInsert = (payload: RealtimePayload) =>
        handleInsert(payload as unknown as { new: NotificationRow });
      const onUpdate = (payload: RealtimePayload) =>
        handleUpdate(payload as unknown as { new: NotificationRow });

      // Use the modern Channel API with specific filters
      channel = supabase
        .channel(`notifications-stream-${user.id}`)
        // 1. Personal notifications
        .on(
          "postgres_changes",
          {
            event: "INSERT",
            schema: "public",
            table: "notifications",
            filter: `user_id=eq.${user.id}`,
          },
          onInsert,
        )
        // 2. Global user notifications
        .on(
          "postgres_changes",
          {
            event: "INSERT",
            schema: "public",
            table: "notifications",
            filter: "audience=eq.user",
          },
          onInsert,
        );

      // 3. Admin-wide notifications (only if admin)
      if (isAdmin) {
        channel = channel.on(
          "postgres_changes",
          {
            event: "INSERT",
            schema: "public",
            table: "notifications",
            filter: "audience=eq.admin",
          },
          onInsert,
        );
      }

      // 4. Handle updates (marks as read)
      channel = channel
        .on(
          "postgres_changes",
          { event: "UPDATE", schema: "public", table: "notifications" },
          onUpdate,
        )
        .subscribe((status) => {
          if (status === "SUBSCRIBED") {
            if (wasDisconnectedRef.current) {
              toast.success("Reconnected to live notifications");
              wasDisconnectedRef.current = false;
              refresh();
            }
            reconnectAttemptsRef.current = 0;
          } else if (
            status === "CHANNEL_ERROR" ||
            status === "TIMED_OUT" ||
            status === "CLOSED"
          ) {
            scheduleReconnect();
          }
        });
    };

    const scheduleReconnect = () => {
      if (cancelled || reconnectTimer) return;
      if (!wasDisconnectedRef.current) {
        wasDisconnectedRef.current = true;
        toast.error("Notifications disconnected. Reconnecting…");
      }
      const attempt = reconnectAttemptsRef.current++;
      const delay = Math.min(30000, 1000 * Math.pow(2, attempt));
      reconnectTimer = window.setTimeout(() => {
        reconnectTimer = null;
        if (channel) supabase.removeChannel(channel);
        connect();
      }, delay);
    };

    const handleOnline = () => {
      if (wasDisconnectedRef.current) scheduleReconnect();
    };
    const handleOffline = () => {
      wasDisconnectedRef.current = true;
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    connect();

    return () => {
      cancelled = true;
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
      if (channel) supabase.removeChannel(channel);
    };
    // isAdmin must be in deps: when admin status is resolved after mount, the channel
    // needs to re-subscribe so it includes the admin-audience filter.
  }, [user, queryClient, refresh, isAdmin]);

  const markRead = async (id: string) => {
    const now = new Date().toISOString();
    // Guard the DB write before touching the cache so a failed update never
    // leaves the UI showing a notification as read when it isn't.
    const { error: updateError } = await supabase
      .from("notifications")
      .update({ read_at: now })
      .eq("id", id);
    if (updateError) {
      toast.error("Couldn't mark notification as read", {
        description: updateError.message,
      });
      return;
    }
    queryClient.setQueryData(
      ["notifications", user?.id, isAdmin],
      (prev: NotificationRow[] = []) =>
        prev.map((n) => (n.id === id ? { ...n, read_at: now } : n)),
    );
  };

  const markAllRead = async (audience?: "user" | "admin") => {
    const now = new Date().toISOString();
    const ids = items
      .filter((n) => !n.read_at && (!audience || n.audience === audience))
      .map((n) => n.id);
    if (!ids.length) return;
    const { error: updateError } = await supabase
      .from("notifications")
      .update({ read_at: now })
      .in("id", ids);
    if (updateError) {
      toast.error("Couldn't mark notifications as read", {
        description: updateError.message,
      });
      return;
    }
    queryClient.setQueryData(
      ["notifications", user?.id, isAdmin],
      (prev: NotificationRow[] = []) =>
        prev.map((n) => (ids.includes(n.id) ? { ...n, read_at: now } : n)),
    );
  };

  const userItems = useMemo(
    () => items.filter((n) => n.audience === "user"),
    [items],
  );
  const adminItems = useMemo(
    () => (isAdmin ? items.filter((n) => n.audience === "admin") : []),
    [items, isAdmin],
  );
  const unreadCount = useMemo(
    () => items.filter((n) => !n.read_at).length,
    [items],
  );

  return {
    items,
    userItems,
    adminItems,
    loading,
    error,
    unreadCount,
    refresh,
    markRead,
    markAllRead,
  };
}
